import React, { useState, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { AlertTriangle, Undo2, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASON_SUGGESTIONS = [
  'Saisie erronée — montant',
  'Saisie erronée — mauvaise facture',
  'Doublon avec un autre paiement',
  'Annulation client (mauvais virement)',
  'Test produit / saisie de démonstration',
];

/**
 * Modal de réversion de paiement.
 *
 * Props :
 *  - open: boolean
 *  - onClose: () => void
 *  - onSuccess: (response) => void
 *  - type: 'contract' | 'spot'
 *  - resourceId: invoiceId (contrat) ou operationId (spot)
 *  - resource: dict avec {invoiceNumber, totalAmount, amountPaid, paidAt}
 *  - maxDaysOverride: number (optionnel)
 */
export default function PaymentReversalModal({
  open,
  onClose,
  onSuccess,
  type,
  resourceId,
  resource = {},
  maxDaysOverride,
}) {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const daysSincePayment = useMemo(() => {
    const dateStr = resource.paidAt || resource.paymentReceivedAt;
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  }, [resource]);

  const maxDays = maxDaysOverride || 90;
  const isWarning = daysSincePayment != null && daysSincePayment >= 60 && daysSincePayment <= maxDays;
  const isBlocked = daysSincePayment != null && daysSincePayment > maxDays;

  const handleSubmit = async () => {
    if (reason.trim().length < 10) {
      toast({ title: 'Motif trop court', description: 'Minimum 10 caractères requis.', variant: 'destructive' });
      return;
    }
    if (isBlocked) return;

    setSubmitting(true);
    try {
      const url = type === 'contract'
        ? `${API}/contract-invoices/${resourceId}/reverse-payment`
        : `${API}/financial/${resourceId}/reverse-payment`;
      const res = await axios.post(url, { reason: reason.trim() });
      toast({
        title: 'Paiement annulé',
        description: type === 'contract'
          ? `Facture remise en statut "${res.data?.invoice?.status || 'sent'}"`
          : `Opération remise en attente de paiement`,
      });
      if (onSuccess) onSuccess(res.data);
      setReason('');
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Erreur lors de l\'annulation du paiement.';
      toast({ title: 'Annulation impossible', description: detail, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="payment-reversal-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="w-5 h-5 text-red-600" />
            Annuler le paiement
          </DialogTitle>
          <DialogDescription>
            Cette action est tracée dans l'audit log (`INVOICE_PAYMENT_REVERSED` ou `OPERATION_PAYMENT_REVERSED`).
          </DialogDescription>
        </DialogHeader>

        {/* Récap */}
        <div className="bg-slate-50 rounded-lg p-4 space-y-1 text-sm">
          {resource.invoiceNumber && (
            <div className="flex justify-between">
              <span className="text-slate-500">Référence</span>
              <span className="font-medium">{resource.invoiceNumber}</span>
            </div>
          )}
          {resource.clientName && (
            <div className="flex justify-between">
              <span className="text-slate-500">Client</span>
              <span className="font-medium">{resource.clientName}</span>
            </div>
          )}
          {resource.amountPaid != null && (
            <div className="flex justify-between">
              <span className="text-slate-500">Montant payé</span>
              <span className="font-medium">{(resource.amountPaid || 0).toLocaleString('fr-FR')} XOF</span>
            </div>
          )}
          {daysSincePayment != null && (
            <div className="flex justify-between">
              <span className="text-slate-500">Jours depuis le paiement</span>
              <span className={`font-medium ${isBlocked ? 'text-red-600' : isWarning ? 'text-amber-600' : ''}`}>
                {daysSincePayment} jour(s)
              </span>
            </div>
          )}
        </div>

        {isBlocked && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2 items-start">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <strong>Délai dépassé ({daysSincePayment}j &gt; {maxDays}j).</strong> Pour rester conforme OHADA,
              vous devez émettre un <strong>avoir (credit note)</strong> à la place. Contactez votre comptable.
            </div>
          </div>
        )}

        {isWarning && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2 items-start">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              ⚠️ Plus de 60 jours. Vérifiez la cohérence avec votre comptable avant de valider.
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="reason">Motif de l'annulation <span className="text-red-500">*</span></Label>
          <Textarea
            id="reason"
            placeholder="Ex: Paiement enregistré sur la mauvaise facture (devait être FACT-2026-02-005 client B)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            data-testid="reversal-reason-input"
            disabled={isBlocked}
          />
          <div className="flex flex-wrap gap-1">
            {REASON_SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setReason(s)}
                disabled={isBlocked}
                className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded text-slate-700 disabled:opacity-50"
              >
                {s}
              </button>
            ))}
          </div>
          <div className="text-xs text-slate-500">{reason.length} / 10 minimum</div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="reversal-cancel-btn">Annuler</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || isBlocked || reason.trim().length < 10}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="reversal-confirm-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Annulation…</>
            ) : (
              <><Undo2 className="w-4 h-4 mr-2" /> Confirmer l'annulation</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
